import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios'
import { useAuthStore } from '@/features/auth'

const API_BASE_URL = import.meta.env.DEV ? 'http://localhost:8000/api' : '/api'

/**
 * Extracts a human-readable error message from any thrown value.
 * Use this when displaying API errors in the UI or toasts.
 */
export const extractApiError = (error: unknown): string => {
    if (axios.isAxiosError(error)) {
        const data = error.response?.data
        if (typeof data === 'object' && data !== null && 'message' in data) {
            return String((data as { message: unknown }).message)
        }
        if (typeof data === 'string' && data.length > 0) return data
        if (error.code === 'ECONNABORTED') return 'Request timed out'
        if (!error.response) return 'Network error — check your connection'
        return error.message
    }
    if (error instanceof Error) return error.message
    return 'An unexpected error occurred'
}

const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 10000,
    headers: { 'Content-Type': 'application/json' },
})

// Inject Bearer token from auth store on every request
api.interceptors.request.use(
    (config) => {
        const token = useAuthStore.getState().getToken()
        if (token) {
            config.headers.Authorization = `Bearer ${token}`
        }
        return config
    },
    (error) => Promise.reject(error),
)

// On 401, trigger logout; normalize all errors to Error instances
api.interceptors.response.use(
    (response) => response,
    (error: unknown) => {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
            useAuthStore.getState().logout()
        }
        return Promise.reject(new Error(extractApiError(error)))
    },
)

export const apiService = {
    get:    <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => api.get<T>(url, config),
    post:   <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => api.post<T>(url, data, config),
    put:    <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => api.put<T>(url, data, config),
    delete: <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => api.delete<T>(url, config),
    patch:  <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => api.patch<T>(url, data, config),
}

export default api

// ── Finance API ────────────────────────────────────────────────────────────

import type {
    Person,
    Category,
    CategoryCreate,
    CategoryUpdate,
    Rule,
    RuleCreate,
    RuleUpdate,
    Transaction,
    TransactionCreate,
    TransactionUpdate,
    TransactionFilters,
    TransactionPage,
    ImportSummary,
    Summary,
    CategoryTotal,
    IncomeTotal,
    MonthlyTotal,
} from '@/shared/types/finance'

// People
export const getPeople = () =>
    apiService.get<Person[]>('/people').then((r) => r.data)

// Categories
export const getCategories = () =>
    apiService.get<Category[]>('/categories').then((r) => r.data)

export const createCategory = (data: CategoryCreate) =>
    apiService.post<Category>('/categories', data).then((r) => r.data)

export const updateCategory = (id: number, data: CategoryUpdate) =>
    apiService.patch<Category>(`/categories/${id}`, data).then((r) => r.data)

export const deleteCategory = (id: number) =>
    apiService.delete(`/categories/${id}`).then((r) => r.data)

// Rules
export const getRules = () =>
    apiService.get<Rule[]>('/rules').then((r) => r.data)

export const createRule = (data: RuleCreate) =>
    apiService.post<Rule>('/rules', data).then((r) => r.data)

export const updateRule = (id: number, data: RuleUpdate) =>
    apiService.patch<Rule>(`/rules/${id}`, data).then((r) => r.data)

export const deleteRule = (id: number) =>
    apiService.delete(`/rules/${id}`).then((r) => r.data)

// Transactions
export const getTransactions = (filters?: TransactionFilters) =>
    apiService
        // A longer timeout than the 10s global default: this is the one endpoint
        // that can return a few thousand rows.
        .get<TransactionPage>('/transactions', { params: filters, timeout: 30_000 })
        .then((r) => r.data)

export const createTransaction = (data: TransactionCreate) =>
    apiService.post<Transaction>('/transactions', data).then((r) => r.data)

export const updateTransaction = (id: number, data: TransactionUpdate) =>
    apiService.patch<Transaction>(`/transactions/${id}`, data).then((r) => r.data)

export const deleteTransaction = (id: number) =>
    apiService.delete(`/transactions/${id}`).then((r) => r.data)

export const importTransactions = (
    file: File,
    importer: string,
    payerId: number
) => {
    const formData = new FormData()
    formData.append('file', file)
    return apiService
        .post<ImportSummary>(
            `/transactions/import?importer=${importer}&payer_id=${payerId}`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
        )
        .then((r) => r.data)
}

export const reapplyRules = () =>
    apiService.post('/transactions/reapply-rules').then((r) => r.data)

// Summary & Analytics
export const getSummary = (start?: string, end?: string) =>
    apiService
        .get<Summary>('/summary', { params: { start, end } })
        .then((r) => r.data)

export const getCategoryTotals = (start?: string, end?: string, payerId?: number) =>
    apiService
        .get<CategoryTotal[]>('/analytics/category-totals', {
            params: { start, end, payer_id: payerId },
        })
        .then((r) => r.data)

export const getIncomeTotals = (start?: string, end?: string) =>
    apiService
        .get<IncomeTotal[]>('/analytics/income-totals', { params: { start, end } })
        .then((r) => r.data)

export const getMonthlyTotals = (
    start?: string,
    end?: string,
    payerId?: number
) =>
    apiService
        .get<MonthlyTotal[]>('/analytics/monthly-totals', {
            params: { start, end, payer_id: payerId },
        })
        .then((r) => r.data)
